import {
    type CSSProperties,
    useEffect,
    useState
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    NCoreUIKitTheme,
    Command,
    Button,
    Avatar
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    type DocsStyleTokens,
    InlineCode,
    BulletList,
    Paragraph,
    CodeBlock,
    DemoFrame,
    Article,
    Preview,
    Section
} from "../../components/content";
import {
    SearchIcon,
    StarIcon,
    UserIcon,
    BellIcon,
    HomeIcon,
    FileIcon,
    PlusIcon
} from "../../anatomy/icons";

const NAME = "Command";

const USAGE_CODE = `const [lastCommand, setLastCommand] = useState("");

<Command
    variant="inline"
>
    <Command.Input
        placeholder="Search commands…"
    />
    <Command.List>
        <Command.Group
            title="Go to"
        >
            <Command.Item
                onPress={() => setLastCommand("Home")}
                keywords={["start"]}
                icon={HomeIcon}
                title="Home"
            />
            <Command.Item
                onPress={() => setLastCommand("Notifications")}
                keywords={["alerts"]}
                icon={BellIcon}
                title="Notifications"
            />
        </Command.Group>
        <Command.Separator/>
        <Command.Group
            title="Actions"
        >
            <Command.Item
                onPress={() => setLastCommand("New file")}
                icon={PlusIcon}
                title="New file"
                shortcut="mod+N"
            />
        </Command.Group>
    </Command.List>
    <Command.Footer/>
</Command>`;

const FILTER_CODE = `<Command.Group
    displayBehaviour="idle"
    title="Recent"
>
    ...
</Command.Group>
<Command.Group
    title="Settings"
>
    <Command.Item
        keywords={["theme", "appearance"]}
        parentTitle="Settings"
        title="Dark mode"
    />
</Command.Group>`;

const REMOTE_CODE = `<Command
    filterBehaviour="manual"
    isLoading={isSearching}
>
    <Command.Input
        onChangeText={setQuery}
        value={query}
    />
    <Command.List>
        <Command.Empty/>
        <Command.Loading
            title="Searching…"
        />
        {
            results.map((name) => <Command.Item
                title={name}
                key={name}
            />)
        }
    </Command.List>
</Command>`;

const PAGE_CODE = `<Command
    onPageChange={setPages}
    pages={pages}
>
    <Command.Input/>
    <Command.List>
        <Command.Item
            title="Change theme"
            pageKey="theme"
        />
    </Command.List>
    <Command.Page
        title="Theme"
        pageKey="theme"
    >
        <Command.Item
            onPress={() => setTheme("dark")}
            isSelected={theme === "dark"}
            title="Dark"
        />
    </Command.Page>
</Command>`;

const DIALOG_CODE = `const [isOpen, setIsOpen] = useState(false);

<Command.Dialog
    onOpenChange={setIsOpen}
    title="Command palette"
    globalShortcut="mod+K"
    isOpen={isOpen}
>
    <Command
        variant="dialog"
    >
        <Command.Input/>
        <Command.List>
            ...
        </Command.List>
        <Command.Footer/>
    </Command>
</Command.Dialog>`;

const DIALOG_STANDALONE_CODE = `import {
    CommandDialog,
    Command
} from "ncore-ui-kit";

<CommandDialog
    onOpenChange={setIsOpen}
    title="Command palette"
    isOpen={isOpen}
>
    <Command
        variant="dialog"
    >
        ...
    </Command>
</CommandDialog>`;

const getQuickStyle = ({
    spaces
}: DocsStyleTokens): CSSProperties => ({
    justifyContent: "center",
    gap: spaces.spacingSm,
    flexWrap: "wrap",
    display: "flex"
});

const THEMES = [
    "system",
    "light",
    "dark"
] as const;

const LANGUAGES = [
    "english",
    "turkish"
] as const;

const REMOTE_PEOPLE = [
    "Ada Lovelace",
    "Grace Hopper",
    "Alan Turing",
    "Katherine Johnson",
    "Margaret Hamilton",
    "Dennis Ritchie"
];

const SEARCH_DELAY = 700;

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("commandPage.itemsTitle"),
        id: "items"
    },
    {
        title: t("commandPage.filteringTitle"),
        id: "filtering"
    },
    {
        title: t("commandPage.remoteTitle"),
        id: "remote"
    },
    {
        title: t("commandPage.pagesTitle"),
        id: "pages"
    },
    {
        title: t("commandPage.dialogTitle"),
        id: "dialog"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const CommandPage = () => {
    const docsTokens = NCoreUIKitTheme.useContext();

    const {
        t
    } = useDocsI18n();

    const [
        lastCommand,
        setLastCommand
    ] = useState("");

    const [
        paletteCommand,
        setPaletteCommand
    ] = useState("");

    const [
        filterQuery,
        setFilterQuery
    ] = useState("");

    const [
        theme,
        setTheme
    ] = useState<(typeof THEMES)[number]>("system");

    const [
        language,
        setLanguage
    ] = useState<(typeof LANGUAGES)[number]>("english");

    const [
        remoteQuery,
        setRemoteQuery
    ] = useState("");

    const [
        searchedQuery,
        setSearchedQuery
    ] = useState("");

    const [
        isPaletteVisible,
        setIsPaletteVisible
    ] = useState(false);

    const [
        isSheetVisible,
        setIsSheetVisible
    ] = useState(false);

    const isSearching = remoteQuery !== searchedQuery;

    useEffect(() => {
        if(remoteQuery === searchedQuery) {
            return;
        }

        const timer = window.setTimeout(() => setSearchedQuery(remoteQuery), SEARCH_DELAY);

        return () => {
            window.clearTimeout(timer);
        };
    }, [
        remoteQuery,
        searchedQuery
    ]);

    const themeLabels = {
        system: t("commandPage.themeSystem"),
        light: t("commandPage.themeLight"),
        dark: t("commandPage.themeDark")
    };

    const languageLabels = {
        english: t("commandPage.languageEnglish"),
        turkish: t("commandPage.languageTurkish")
    };

    const remoteResults = REMOTE_PEOPLE.filter((name) => name.toLowerCase().includes(searchedQuery.trim().toLowerCase()));

    const quickQueries = [
        t("commandPage.queryDiacritic"),
        "an",
        t("commandPage.keywordTheme"),
        t("commandPage.querySettings")
    ];

    const handleCommand = (title: string) => setLastCommand(title);

    const handlePaletteCommand = (title: string) => setPaletteCommand(title);

    const handleThemeChange = (key: (typeof THEMES)[number]) => setTheme(key);

    const handleLanguageChange = (key: (typeof LANGUAGES)[number]) => setLanguage(key);

    const renderPalette = () => <Command
        variant="dialog"
    >
        <Command.Input
            placeholder={t("commandPage.searchPlaceholder")}
        />
        <Command.List>
            <Command.Group
                title={t("commandPage.groupGoTo")}
            >
                <Command.Item
                    onPress={() => handlePaletteCommand(t("commandPage.home"))}
                    title={t("commandPage.home")}
                    icon={HomeIcon}
                />
                <Command.Item
                    onPress={() => handlePaletteCommand(t("sample.item1"))}
                    title={t("sample.item1")}
                    icon={UserIcon}
                />
                <Command.Item
                    onPress={() => handlePaletteCommand(t("commandPage.notifications"))}
                    title={t("commandPage.notifications")}
                    icon={BellIcon}
                />
            </Command.Group>
            <Command.Separator/>
            <Command.Group
                title={t("commandPage.groupActions")}
            >
                <Command.Item
                    onPress={() => handlePaletteCommand(t("commandPage.newFile"))}
                    title={t("commandPage.newFile")}
                    shortcut="mod+N"
                    icon={PlusIcon}
                />
                <Command.Item
                    onPress={() => handlePaletteCommand(t("commandPage.favourite"))}
                    title={t("commandPage.favourite")}
                    icon={StarIcon}
                />
            </Command.Group>
        </Command.List>
        <Command.Footer/>
    </Command>;

    return <Article>
        <ComponentHeader
            lead={t("commandPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("commandPage.overviewText")}
            </Paragraph>
            <Paragraph>
                {t("commandPage.overviewStructure")}
            </Paragraph>
            <Preview
                caption={lastCommand ? t("commandPage.lastCommand", [
                    lastCommand
                ]) : t("commandPage.overviewCaption")}
                isStacked={true}
            >
                <DemoFrame>
                    <Command
                        variant="inline"
                    >
                        <Command.Input
                            placeholder={t("commandPage.searchPlaceholder")}
                        />
                        <Command.List>
                            <Command.Empty
                                title={t("commandPage.noMatches")}
                            />
                            <Command.Group
                                title={t("commandPage.groupGoTo")}
                            >
                                <Command.Item
                                    onPress={() => handleCommand(t("commandPage.home"))}
                                    title={t("commandPage.home")}
                                    icon={HomeIcon}
                                />
                                <Command.Item
                                    onPress={() => handleCommand(t("commandPage.notifications"))}
                                    title={t("commandPage.notifications")}
                                    keywords={[
                                        t("commandPage.keywordAlerts")
                                    ]}
                                    icon={BellIcon}
                                />
                            </Command.Group>
                            <Command.Separator/>
                            <Command.Group
                                title={t("commandPage.groupActions")}
                            >
                                <Command.Item
                                    onPress={() => handleCommand(t("commandPage.newFile"))}
                                    keywords={[
                                        t("commandPage.keywordCreate")
                                    ]}
                                    title={t("commandPage.newFile")}
                                    shortcut="mod+N"
                                    icon={PlusIcon}
                                />
                            </Command.Group>
                        </Command.List>
                        <Command.Footer/>
                    </Command>
                </DemoFrame>
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("commandPage.itemsTitle")}
            id="items"
        >
            <Paragraph>
                {t("commandPage.itemsText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <DemoFrame>
                    <Command
                        variant="inline"
                    >
                        <Command.Input
                            placeholder={t("commandPage.searchPlaceholder")}
                        />
                        <Command.List>
                            <Command.Group
                                title={t("commandPage.groupFiles")}
                            >
                                <Command.Item
                                    description={t("commandPage.reportDescription")}
                                    title={t("commandPage.quarterlyReport")}
                                    meta={t("commandPage.yesterday")}
                                    icon={FileIcon}
                                />
                                <Command.Item
                                    title={t("commandPage.newFile")}
                                    shortcut="mod+N"
                                    icon={PlusIcon}
                                />
                                <Command.Item
                                    description={t("commandPage.archiveReason")}
                                    title={t("commandPage.archiveProject")}
                                    isDisabled={true}
                                />
                                <Command.Item
                                    title={t("commandPage.deleteProject")}
                                    type="danger"
                                />
                            </Command.Group>
                            <Command.Separator/>
                            <Command.Group
                                title={t("commandPage.groupMore")}
                            >
                                <Command.Item
                                    leading={<Avatar
                                        title={t("commandPage.personDiacritic")}
                                        size="xxSmall"
                                    />}
                                    description={t("commandPage.personRole")}
                                    title={t("commandPage.personDiacritic")}
                                />
                                <Command.Item
                                    title={t("commandPage.documentation")}
                                    isExternal={true}
                                />
                                <Command.Item
                                    title={t("commandPage.compactDensity")}
                                    isSelected={true}
                                />
                            </Command.Group>
                        </Command.List>
                    </Command>
                </DemoFrame>
            </Preview>
        </Section>
        <Section
            title={t("commandPage.filteringTitle")}
            id="filtering"
        >
            <Paragraph>
                {t("commandPage.filteringText")}
            </Paragraph>
            <Preview
                caption={t("commandPage.filteringCaption")}
                isStacked={true}
            >
                <DemoFrame>
                    <Command
                        variant="inline"
                    >
                        <Command.Input
                            placeholder={t("commandPage.searchPlaceholder")}
                            onChangeText={setFilterQuery}
                            value={filterQuery}
                        />
                        <Command.List>
                            <Command.Empty
                                title={t("commandPage.noMatches")}
                            />
                            <Command.Group
                                title={t("commandPage.groupRecent")}
                                displayBehaviour="idle"
                            >
                                <Command.Item
                                    title={t("commandPage.quarterlyReport")}
                                    icon={FileIcon}
                                />
                                <Command.Item
                                    title={t("commandPage.launchPlan")}
                                    icon={FileIcon}
                                />
                            </Command.Group>
                            <Command.Group
                                title={t("commandPage.groupPeople")}
                            >
                                <Command.Item
                                    title="Hannah Arendt"
                                    icon={UserIcon}
                                />
                                <Command.Item
                                    title="Anita Borg"
                                    icon={UserIcon}
                                />
                                <Command.Item
                                    title={t("commandPage.personDiacritic")}
                                    icon={UserIcon}
                                />
                            </Command.Group>
                            <Command.Group
                                title={t("commandPage.groupSettings")}
                            >
                                <Command.Item
                                    parentTitle={t("commandPage.groupSettings")}
                                    title={t("commandPage.notifications")}
                                    icon={BellIcon}
                                />
                                <Command.Item
                                    parentTitle={t("commandPage.groupSettings")}
                                    keywords={[
                                        t("commandPage.keywordTheme"),
                                        t("commandPage.keywordNight")
                                    ]}
                                    title={t("commandPage.darkMode")}
                                    icon={StarIcon}
                                />
                            </Command.Group>
                        </Command.List>
                        <Command.Footer/>
                    </Command>
                </DemoFrame>
                <div
                    style={getQuickStyle(docsTokens)}
                >
                    {
                        quickQueries.map((query) => <Button
                            onPress={() => setFilterQuery(query)}
                            variant="tonal"
                            type="neutral"
                            title={query}
                            size="small"
                            key={query}
                        />)
                    }
                </div>
            </Preview>
            <CodeBlock
                code={FILTER_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("commandPage.remoteTitle")}
            id="remote"
        >
            <Paragraph>
                {t("commandPage.remoteText")}
            </Paragraph>
            <Preview
                caption={t("commandPage.remoteCaption")}
                isStacked={true}
            >
                <DemoFrame>
                    <Command
                        filterBehaviour="manual"
                        isLoading={isSearching}
                        variant="inline"
                    >
                        <Command.Input
                            placeholder={t("commandPage.searchPeople")}
                            onChangeText={setRemoteQuery}
                            value={remoteQuery}
                        />
                        <Command.List>
                            <Command.Empty/>
                            <Command.Loading
                                title={t("commandPage.searching")}
                            />
                            {
                                remoteResults.map((name) => <Command.Item
                                    icon={UserIcon}
                                    title={name}
                                    key={name}
                                />)
                            }
                        </Command.List>
                    </Command>
                </DemoFrame>
            </Preview>
            <CodeBlock
                code={REMOTE_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("commandPage.pagesTitle")}
            id="pages"
        >
            <Paragraph>
                {t("commandPage.pagesText")}
            </Paragraph>
            <Preview
                caption={t("commandPage.pagesCaption")}
                isStacked={true}
            >
                <DemoFrame>
                    <Command
                        variant="inline"
                    >
                        <Command.Input
                            placeholder={t("commandPage.searchPlaceholder")}
                        />
                        <Command.List>
                            <Command.Group
                                title={t("commandPage.groupSettings")}
                            >
                                <Command.Item
                                    title={t("commandPage.changeTheme")}
                                    keywords={[
                                        t("commandPage.keywordTheme")
                                    ]}
                                    pageKey="theme"
                                />
                                <Command.Item
                                    title={t("commandPage.changeLanguage")}
                                    pageKey="language"
                                />
                                <Command.Item
                                    title={t("commandPage.documentation")}
                                    isExternal={true}
                                />
                            </Command.Group>
                        </Command.List>
                        <Command.Page
                            title={t("commandPage.themePage")}
                            pageKey="theme"
                        >
                            {
                                THEMES.map((key) => <Command.Item
                                    onPress={() => handleThemeChange(key)}
                                    isSelected={theme === key}
                                    title={themeLabels[key]}
                                    key={key}
                                />)
                            }
                        </Command.Page>
                        <Command.Page
                            title={t("commandPage.languagePage")}
                            pageKey="language"
                        >
                            {
                                LANGUAGES.map((key) => <Command.Item
                                    onPress={() => handleLanguageChange(key)}
                                    isSelected={language === key}
                                    title={languageLabels[key]}
                                    key={key}
                                />)
                            }
                        </Command.Page>
                        <Command.Footer/>
                    </Command>
                </DemoFrame>
            </Preview>
            <CodeBlock
                code={PAGE_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("commandPage.dialogTitle")}
            id="dialog"
        >
            <Paragraph>
                {t("commandPage.dialogText")}
            </Paragraph>
            <Preview
                caption={paletteCommand ? t("commandPage.lastCommand", [
                    paletteCommand
                ]) : t("commandPage.dialogCaption")}
            >
                <Button
                    onPress={() => setIsPaletteVisible(true)}
                    title={t("commandPage.openPalette")}
                    icon={SearchIcon}
                    variant="outline"
                    type="neutral"
                />
                <Button
                    onPress={() => setIsSheetVisible(true)}
                    title={t("commandPage.openSheet")}
                    icon={SearchIcon}
                    variant="outline"
                    type="neutral"
                />
                <Command.Dialog
                    title={t("commandPage.dialogTitle")}
                    onOpenChange={setIsPaletteVisible}
                    isOpen={isPaletteVisible}
                    globalShortcut=""
                >
                    {renderPalette()}
                </Command.Dialog>
                <Command.Dialog
                    title={t("commandPage.dialogTitle")}
                    onOpenChange={setIsSheetVisible}
                    isOpen={isSheetVisible}
                    globalShortcut=""
                    variant="sheet"
                >
                    {renderPalette()}
                </Command.Dialog>
            </Preview>
            <CodeBlock
                code={DIALOG_CODE}
                language="tsx"
            />
            <Paragraph>
                {t("commandPage.dialogStandaloneText")}
            </Paragraph>
            <CodeBlock
                code={DIALOG_STANDALONE_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("commandPage.a11yInput"),
                    t("commandPage.a11yList"),
                    t("commandPage.a11yKeyboard"),
                    t("commandPage.a11yLive"),
                    t("commandPage.a11yPages"),
                    t("commandPage.a11yDialog"),
                    t("commandPage.a11yWarn")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        type: "\"inline\" | \"dialog\" | \"sheet\"",
                        description: t("commandPage.propVariant"),
                        defaultValue: "\"inline\"",
                        name: "variant"
                    },
                    {
                        description: t("commandPage.propFilterBehaviour"),
                        type: "\"auto\" | \"manual\"",
                        defaultValue: "\"auto\"",
                        name: "filterBehaviour"
                    },
                    {
                        description: t("commandPage.propIsLoading"),
                        defaultValue: "false",
                        name: "isLoading",
                        type: "boolean"
                    },
                    {
                        description: t("commandPage.propPages"),
                        type: "Array<string>",
                        name: "pages"
                    },
                    {
                        description: t("commandPage.propOnPageChange"),
                        type: "(pages: Array<string>) => void",
                        name: "onPageChange"
                    },
                    {
                        description: t("commandPage.propStyle"),
                        type: "StyleProp<ViewStyle>",
                        name: "style"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("commandPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("commandPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("commandPage.propsNote")}
            </Paragraph>
            <Paragraph>
                <InlineCode>
                    Command.Dialog
                </InlineCode>
                {" "}
                {t("commandPage.partDialogText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("commandPage.propDialogIsOpen"),
                        name: "isOpen",
                        type: "boolean"
                    },
                    {
                        description: t("commandPage.propDialogIsDefaultOpen"),
                        defaultValue: "false",
                        name: "isDefaultOpen",
                        type: "boolean"
                    },
                    {
                        description: t("commandPage.propDialogOnOpenChange"),
                        type: "(isOpen: boolean) => void",
                        name: "onOpenChange"
                    },
                    {
                        description: t("commandPage.propDialogOnClosed"),
                        type: "(props: { id: string }) => void",
                        name: "onClosed"
                    },
                    {
                        description: t("commandPage.propDialogGlobalShortcut"),
                        defaultValue: "\"mod+K\"",
                        name: "globalShortcut",
                        type: "string"
                    },
                    {
                        description: t("commandPage.propDialogVariant"),
                        type: "\"auto\" | \"dialog\" | \"sheet\"",
                        defaultValue: "\"auto\"",
                        name: "variant"
                    },
                    {
                        description: t("commandPage.propDialogTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("commandPage.propDialogOnOverlayPress"),
                        type: "(props: { closeAnimation }) => void",
                        name: "onOverlayPress"
                    },
                    {
                        description: t("commandPage.propDialogId"),
                        type: "string",
                        name: "id"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("commandPage.dialogNote")}
            </Paragraph>
            <Paragraph>
                <InlineCode>
                    Command.Input
                </InlineCode>
                {" "}
                {t("commandPage.partInputText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("commandPage.propInputPlaceholder"),
                        name: "placeholder",
                        type: "string"
                    },
                    {
                        description: t("commandPage.propInputValue"),
                        type: "string",
                        name: "value"
                    },
                    {
                        description: t("commandPage.propInputOnChangeText"),
                        type: "(text: string) => void",
                        name: "onChangeText"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("commandPage.inputNote")}
            </Paragraph>
            <Paragraph>
                <InlineCode>
                    Command.Item
                </InlineCode>
                {" "}
                {t("commandPage.partItemText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("commandPage.propItemTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("commandPage.propItemIcon"),
                        type: "NCoreUIKitIcon",
                        name: "icon"
                    },
                    {
                        description: t("commandPage.propItemDescription"),
                        name: "description",
                        type: "string"
                    },
                    {
                        description: t("commandPage.propItemMeta"),
                        type: "string",
                        name: "meta"
                    },
                    {
                        description: t("commandPage.propItemShortcut"),
                        name: "shortcut",
                        type: "string"
                    },
                    {
                        description: t("commandPage.propItemType"),
                        type: "\"neutral\" | \"danger\"",
                        defaultValue: "\"neutral\"",
                        name: "type"
                    },
                    {
                        description: t("commandPage.propItemIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        description: t("commandPage.propItemIsSelected"),
                        defaultValue: "false",
                        name: "isSelected",
                        type: "boolean"
                    },
                    {
                        description: t("commandPage.propItemIsExternal"),
                        defaultValue: "false",
                        name: "isExternal",
                        type: "boolean"
                    },
                    {
                        description: t("commandPage.propItemLeading"),
                        type: "ReactNode",
                        name: "leading"
                    },
                    {
                        description: t("commandPage.propItemKeywords"),
                        type: "Array<string>",
                        name: "keywords"
                    },
                    {
                        description: t("commandPage.propItemParentTitle"),
                        name: "parentTitle",
                        type: "string"
                    },
                    {
                        description: t("commandPage.propItemPageKey"),
                        name: "pageKey",
                        type: "string"
                    },
                    {
                        type: "(props: { isNewTab: boolean }) => void",
                        description: t("commandPage.propItemOnPress"),
                        name: "onPress"
                    }
                ]}
            />
            <Paragraph>
                <InlineCode>
                    Command.Group
                </InlineCode>
                {" "}
                {t("commandPage.partGroupText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("commandPage.propGroupTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("commandPage.propGroupDisplayBehaviour"),
                        type: "\"always\" | \"idle\"",
                        defaultValue: "\"always\"",
                        name: "displayBehaviour"
                    }
                ]}
            />
            <Paragraph>
                <InlineCode>
                    Command.Page
                </InlineCode>
                {" "}
                {t("commandPage.partPageText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("commandPage.propPagePageKey"),
                        name: "pageKey",
                        type: "string"
                    },
                    {
                        description: t("commandPage.propPageTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("commandPage.propPagePlaceholder"),
                        name: "placeholder",
                        type: "string"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("commandPage.partsNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default CommandPage;
